'use client';

import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useRef } from 'react';
import { ViewParametersContext } from '@/contexts/navigation/viewParametersContext';
import type { SetViewParams } from '@/contexts/navigation/viewParametersContextTypes';
import { isCondition } from '@/types/advanceFilter';
const useViewParametersSafe = () => {
  const context = useContext(ViewParametersContext);
  if (!context) {
    throw new Error("useViewParameters must be used within a ViewParametersProvider");
  }
  return context;
};
export const useViewParameters = initialValues => {
  const $ = _c(6);
  const context = useViewParametersSafe();
  if (initialValues) {
    context.registerDefaults(initialValues);
  }
  const didWriteDefaults = useRef(false);
  let t0;
  let t1;
  if ($[0] !== context || $[1] !== initialValues) {
    t0 = () => {
      if (initialValues && !didWriteDefaults.current) {
        didWriteDefaults.current = true;
        context.commitDefaults();
      }
    };
    t1 = [initialValues, context];
    $[0] = context;
    $[1] = initialValues;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== context) {
    t2 = extractState(context.params(), context.params, context.setParams);
    $[4] = context;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const extractState = (state: Record<string, unknown>, getAll: () => Record<string, unknown>, setParams: SetViewParams) => {
  const {
    pageIndex,
    pageSize,
    sort,
    query,
    id,
    ...rest
  } = state;
  return {
    pageIndex: typeof pageIndex === 'number' ? pageIndex : undefined,
    pageSize: typeof pageSize === 'number' ? pageSize : undefined,
    sort: typeof sort === 'string' && sort !== '' ? sort : undefined,
    query: isCondition(query) ? query : undefined,
    id: id as string,
    ...rest,
    getAll,
    setParams,
    removeParam: (param: string) => {
      setParams(state => Object.fromEntries(Object.entries(state).filter(([key]) => key !== param)));
    }
  };
};